'use client';

import { useState, useEffect } from 'react';

import { Post } from '@/types/models';
import BlogPostCard from './BlogPostCard';

interface MarketUpdatesInteractiveProps {
  initialPosts: Post[];
  className?: string;
}

const MarketUpdatesInteractive = ({
  initialPosts,
  className = '',
}: MarketUpdatesInteractiveProps) => {
  const [mounted, setMounted] = useState(false);
  const [posts] = useState<Post[]>(initialPosts);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 10;

  useEffect(() => {
    setMounted(true);
  }, []);

  // Filter posts based on search only
  const filteredPosts =
    posts?.filter((post) => {
      if (searchQuery === '') return true;
      return (
        post?.title?.toLowerCase()?.includes(searchQuery.toLowerCase()) ||
        post?.excerpt?.toLowerCase()?.includes(searchQuery.toLowerCase()) ||
        post?.keywords?.some((keyword) =>
          keyword?.toLowerCase()?.includes(searchQuery.toLowerCase())
        )
      );
    }) || [];

  // Pagination
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = filteredPosts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);

  if (!mounted) {
    return null;
  }

  return (
    <div className={`min-h-screen bg-background ${className}`}>
      {/* Simple Header */}
      <section className="bg-card border-b border-border py-8">
        <div className="max-w-4xl mx-auto px-6">
          <h1 className="text-5xl font-bold text-primary mb-2">Resources</h1>
          <p className="text-lg text-muted-foreground">
            Seller guides, homeowner education, and market updates from Silk Solutions.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6">
          {/* Optional Search */}
          <div className="mb-8">
            <input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-4 py-3 border border-border bg-input rounded-lg text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary/25 focus:border-primary"
            />
          </div>

          {/* Results Count */}
          <div className="mb-6">
            <p className="text-muted-foreground">
              {currentPosts.length} {currentPosts.length === 1 ? 'article' : 'articles'}
            </p>
          </div>

          {/* Blog Posts List */}
          {currentPosts.length > 0 ? (
            <div className="space-y-6 mb-12">
              {currentPosts.map((post) => (
                <BlogPostCard key={post?.id} post={post} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground text-lg">No articles found matching your search.</p>
              <button
                onClick={() => setSearchQuery('')}
                className="mt-4 text-primary hover:text-plum-deep font-medium"
              >
                Clear search
              </button>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center space-x-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 border border-border rounded-md bg-card hover:bg-surface disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-4 py-2 border rounded-md ${
                    currentPage === page
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'border-border bg-card hover:bg-surface'
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border border-border rounded-md bg-card hover:bg-surface disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default MarketUpdatesInteractive;
